import { t } from '../i18n';
import { useCallback, useEffect, useState } from 'react';
import { formatMinor, type CurrencyCode, type PaymentPatchDto, type PaymentStateDto } from '@trestly/shared';
import {
  useCategories,
  useCreatePayment,
  useSetPaymentPaid,
  useUpdatePayment,
} from '../api/hooks';
import { AmountInput, tryParseAmount } from './AmountInput';
import { CategoryChips } from './CategoryChips';
import { CurrencyToggle } from './CurrencyToggle';
import { Field, FieldGroup } from './Field';
import { Sheet } from './Sheet';
import { Spinner } from './Spinner';
import { Toggle } from './Toggle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatIsoDate } from '@/lib/format';
import { confirmAction, haptic } from '@/telegram';

interface PaymentSheetProps {
  open: boolean;
  /** null — создание нового платежа. */
  payment: PaymentStateDto | null;
  period: string;
  onClose: () => void;
}

/**
 * Один лист на создание, правку и отметку оплаты. Раньше это были два разных
 * шита: тап по названию открывал «фактическую сумму», а правка шаблона жила за
 * отдельной иконкой — и было не понять, куда ведёт тап.
 */
export function PaymentSheet({ open, payment, period, onClose }: PaymentSheetProps): JSX.Element {
  const categoriesQuery = useCategories();
  const createPayment = useCreatePayment();
  const updatePayment = useUpdatePayment();
  const markPaid = useSetPaymentPaid(period);

  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>('BYN');
  const [dueDay, setDueDay] = useState('1');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [isRecurring, setIsRecurring] = useState(true);
  const [paid, setPaid] = useState(false);
  const [actual, setActual] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    if (payment) {
      setName(payment.name);
      setAmount(formatMinor(payment.amountMinor, payment.currency));
      setCurrency(payment.currency);
      setDueDay(String(payment.dueDay));
      setCategoryId(payment.categoryId);
      setIsRecurring(payment.isRecurring);
      setPaid(payment.paidAt !== null);
      setActual(
        payment.amountMinorActual === null
          ? ''
          : formatMinor(payment.amountMinorActual, payment.currency),
      );
    } else {
      setName('');
      setAmount('');
      setCurrency('BYN');
      setDueDay('1');
      setCategoryId(null);
      setIsRecurring(true);
      setPaid(false);
      setActual('');
    }
    createPayment.reset();
    updatePayment.reset();
    markPaid.reset();
    // Мутации меняются каждый рендер — намеренно не в зависимостях.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payment]);

  const day = Number(dueDay);
  const dayValid = Number.isInteger(day) && day >= 1 && day <= 31;
  const nameValid = name.trim().length > 0;
  const amountMinor = tryParseAmount(amount, currency);
  const actualMinor = actual.trim() === '' ? null : tryParseAmount(actual, currency);
  const actualValid = actual.trim() === '' || actualMinor !== undefined;
  const busy =
    createPayment.isPending || updatePayment.isPending || markPaid.isPending;
  const error =
    createPayment.error?.message ?? updatePayment.error?.message ?? markPaid.error?.message ?? null;

  const showErrors = submitted;

  const handleSave = useCallback((): void => {
    setSubmitted(true);
    if (amountMinor === undefined || !dayValid || !nameValid || !actualValid) {
      haptic.result('error');
      return;
    }

    const done = {
      onSuccess: () => {
        haptic.result('success');
        onClose();
      },
      onError: () => haptic.result('error'),
    };

    if (!payment) {
      // Новый платёж создаётся неоплаченным: отметку ставят в его месяце.
      createPayment.mutate(
        { name: name.trim(), amountMinor, currency, dueDay: day, isRecurring, categoryId },
        done,
      );
      return;
    }

    const patch: PaymentPatchDto = {
      name: name.trim(),
      amountMinor,
      currency,
      dueDay: day,
      isRecurring,
      categoryId,
    };

    updatePayment.mutate(
      { id: payment.paymentId, patch },
      {
        onSuccess: () => {
          const paidChanged = paid !== (payment.paidAt !== null);
          const actualChanged = actualMinor !== payment.amountMinorActual;
          // Отметку оплаты хранит не шаблон, а состояние периода: сохраняем её
          // отдельным запросом и только если её действительно меняли.
          if (!paidChanged && !actualChanged) {
            haptic.result('success');
            onClose();
            return;
          }
          markPaid.mutate({ id: payment.paymentId, paid, amountMinorActual: actualMinor }, done);
        },
        onError: () => haptic.result('error'),
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    amountMinor,
    dayValid,
    nameValid,
    actualValid,
    actualMinor,
    name,
    currency,
    day,
    isRecurring,
    categoryId,
    payment,
    paid,
    onClose,
  ]);

  const handleArchive = async (): Promise<void> => {
    if (!payment) return;
    const ok = await confirmAction(t('components.payment.deleteConfirm', { name: payment.name }));
    if (!ok) return;
    // Удаление мягкое: archived — история прошлых месяцев не разъезжается.
    updatePayment.mutate(
      { id: payment.paymentId, patch: { archived: true } },
      {
        onSuccess: () => {
          haptic.result('success');
          onClose();
        },
      },
    );
  };

  return (
    <Sheet
      open={open}
      title={payment ? payment.name : t('components.payment.new')}
      description={
        payment ? t('components.payment.planSubtitle', { amount: `${formatMinor(payment.amountMinor, payment.currency)} ${payment.currency}`, date: formatIsoDate(payment.dueDate) }) : undefined
      }
      onClose={onClose}
    >
      <Field label={t('components.name')} error={showErrors && !nameValid ? t('components.nameRequired') : null}>
        <Input
          type="text"
          value={name}
          aria-invalid={showErrors && !nameValid}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('components.payment.namePlaceholder')}
        />
      </Field>

      <AmountInput
        value={amount}
        onChange={setAmount}
        currency={currency}
        showError={showErrors && amountMinor === undefined}
        label={t('components.payment.plannedAmount', { currency })}
        trailing={
          <CurrencyToggle
            value={currency}
            onChange={setCurrency}
            size="compact"
            className="h-15"
            disabled={busy}
          />
        }
      />

      <Field
        label={t('components.payment.day')}
        error={showErrors && !dayValid ? t('components.dayError') : null}
        hint={t('components.payment.dayHint')}
      >
        <Input
          type="number"
          inputMode="numeric"
          min={1}
          max={31}
          value={dueDay}
          aria-invalid={showErrors && !dayValid}
          onChange={(event) => setDueDay(event.target.value)}
        />
      </Field>

      <FieldGroup label={t('components.category')} hint={t('components.payment.categoryHint')}>
        {categoriesQuery.isPending ? (
          <Spinner small />
        ) : categoriesQuery.isError ? (
          <span className="text-[13px] text-destructive">{t('components.payment.categoriesFailed')}</span>
        ) : (
          <CategoryChips
            categories={categoriesQuery.data}
            value={categoryId}
            onChange={setCategoryId}
          />
        )}
      </FieldGroup>

      <div className="mb-3.5 flex items-center justify-between gap-3">
        <span className="text-[15px]">{t('components.payment.monthly')}</span>
        <Toggle
          checked={isRecurring}
          label={t('components.payment.monthly')}
          disabled={busy}
          onChange={setIsRecurring}
        />
      </div>

      {/* Отметка оплаты относится к конкретному периоду и есть только у платежа
          из списка: у нового она появится в своём периоде. */}
      {payment ? (
        <div className="mt-4 rounded-xl bg-muted p-3.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[15px] font-medium">{t('components.payment.paid')}</span>
            <Toggle checked={paid} label={t('components.payment.paid')} disabled={busy} onChange={setPaid} />
          </div>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {paid
              ? t('components.payment.paidOn')
              : t('components.payment.paidHint')}
          </p>

          {paid ? (
            <div className="mt-3">
              <Field
                label={t('components.payment.actualAmount', { currency })}
                hint={t('components.payment.actualHint')}
                error={showErrors && !actualValid ? t('components.amountInvalid') : null}
                className="mb-0"
              >
                <Input
                  inputMode="decimal"
                  placeholder={t('components.payment.actualPlaceholder', { amount: amount || '0.00' })}
                  value={actual}
                  aria-invalid={showErrors && !actualValid}
                  onChange={(event) => setActual(event.target.value)}
                />
              </Field>
            </div>
          ) : null}
        </div>
      ) : null}

      {error ? <div className="mt-3 text-[13px] text-destructive">{error}</div> : null}

      <Button className="mt-4 w-full" disabled={busy} onClick={handleSave}>
        {busy && updatePayment.isPending ? <Spinner small /> : t('components.save')}
      </Button>

      {payment ? (
        <Button
          variant="destructive"
          className="mt-2 w-full"
          disabled={busy}
          onClick={() => void handleArchive()}
        >
          {t('components.payment.delete')}
        </Button>
      ) : null}
    </Sheet>
  );
}
